import type { BridgeRunResult, MCPBridgeSpeech } from './RequestModelingResponse';

type BridgeChatStatus = 'submitted' | 'streaming' | 'ready' | 'error';

type ConvergenceResult = { kind: 'pending' } | { kind: 'resolved' } | { kind: 'error' };

export interface ConvergenceInput {
  status: BridgeChatStatus;
  isAIResponseComplete: boolean;
  processingConfirmed: boolean;
  startedWithError: boolean;
}

// status の状態遷移
//
// ai-sdk の useChat が返す status は単一の状態値だが、以下の理由で扱いが複雑になる。
//
// 【正常な遷移シーケンス】
//   sendMessage 呼び出し
//     → submitted（送信中）
//     → streaming（AI 応答ストリーミング中）
//     → ready（応答完了）
//
//   submitted は makeRequest の冒頭で必ずセットされる。
//   streaming は最初の write() 時のみセットされるため、write を伴わない応答ではスキップされる。
//   processingConfirmed は submitted または streaming のどちらか一方で true になるため、
//   streaming スキップが収束判定に影響することはない。
//
// 【tool call がある場合の中間 ready】
//   AI が tool call を含む応答を返すと、streaming → ready に一度なるが、
//   SDK が自動で次ターンを再送するため、すぐに submitted → streaming → ready と繰り返す。
//   この中間 ready は「収束」ではなく「tool call 処理中の一時状態」であり、
//   isAIResponseComplete で区別する（false なら中間 ready）。
//
// 【processingConfirmed と startedWithError の役割】
//   processingConfirmed: submitted または streaming を観測した（= 現在ターンの処理が始まった）
//   startedWithError:    start 時点で status が error だった（= 残存 error が来ることを把握済み）
//
// 【残存値問題】
//   status は useChat のリアクティブな状態であり、sendMessage を呼ぶ前から値が存在する。
//   MCPBridgeChatSession.start() を呼んだ時点で既に ready や error が流れていることがあり、
//   これらは「前のターンの残存値」であって現在のターンの状態ではない。
//
//   - start 時 ready → sendMessage 後に submitted が来るまでの ready は残存値として無視する
//                      （processingConfirmed=false の間は ready/error を弾く）
//   - start 時 error → 同様に残存 error が流れてくるが、新規 error 直行と区別できない。
//                      start 時点の status を startedWithError に記録し、
//                      processingConfirmed=false かつ startedWithError=true の場合のみ弾く。
//                      start 時 ready から始まって submitted/streaming を経ずに error になった場合は
//                      新規 error として即 reject する（10分タイムアウトを避けるため）。
//
// 【abort/stop の扱い】
//   この関数は「status と AI 応答の状態から収束したか」だけを判定する責務を持つ。
//   ユーザー停止（abort/stop）は呼び出し前に直接 reject 済みで pending=null になっているため、
//   この関数に到達しない。abort 判定をあえてここに持たないのは責務分離のためであり、
//   「abort 判定が漏れている」という誤認を防ぐためにこのコメントで意図を明示している。
export function evaluateBridgeChatConvergence(input: ConvergenceInput): ConvergenceResult {
  const { status, isAIResponseComplete, processingConfirmed, startedWithError } = input;

  if (status === 'submitted' || status === 'streaming') {
    return { kind: 'pending' };
  }

  if (status === 'error') {
    // start 時点で既に error だった画面に流入する残存 error は弾く
    if (!processingConfirmed && startedWithError) {
      return { kind: 'pending' };
    }
    return { kind: 'error' };
  }

  // sendMessage 直後の初期 ready を弾く
  if (!processingConfirmed) {
    return { kind: 'pending' };
  }

  // status === 'ready'
  if (!isAIResponseComplete) {
    return { kind: 'pending' };
  }

  return { kind: 'resolved' };
}

interface HandleStatusChangeInput {
  status: BridgeChatStatus;
  isAIResponseComplete: boolean;
  editToolFailureStopped: boolean;
}

type McpBridgePending = {
  resolve: (value: BridgeRunResult) => void;
  reject: (reason: Error) => void;
  processingConfirmed: boolean;
  startedWithError: boolean;
  timeoutId: ReturnType<typeof setTimeout>;
};

export class BridgeBusyError extends Error {
  constructor() {
    super('AIAssistantChat is busy');
    this.name = 'BridgeBusyError';
  }
}

const DEFAULT_TIMEOUT_MS = 10 * 60 * 1000;

const EDIT_TOOL_STOPPED_NOTICE =
  '入力形式のエラーが連続したため、編集ツールの実行を打ち切りました。' +
  '依頼した内容の一部または全部がビューモデルに適用されていません。' +
  '同じ指示をそのまま送り直しても同じ結果になります。';

export class MCPBridgeChatSession {
  private pending: McpBridgePending | null = null;

  // セッション区間中に積む発話バッファ。start でクリアし、resolvePending 時に返却経路へ載せる。
  // UI 表示用の mcpBridgeLogAtom とは別経路で、MCP 応答契約の一部として使う。
  private messages: MCPBridgeSpeech[] = [];

  get isPending(): boolean {
    return this.pending !== null;
  }

  // 前セッション終了後に遅れて届く onFinish を次セッションに漏らさないため、非 pending 時は捨てる
  appendAssistantMessage(text: string): void {
    if (this.pending === null) return;
    this.messages.push({ text });
  }

  private rejectImmediately(reason: string): void {
    const pending = this.pending;
    if (!pending) return;
    this.rejectPending(new Error(reason), pending);
  }

  private rejectPending(err: Error, expected: McpBridgePending): void {
    if (this.pending !== expected) return;
    clearTimeout(expected.timeoutId);
    this.pending = null;
    expected.reject(err);
  }

  private resolvePending(value: BridgeRunResult, expected: McpBridgePending): void {
    if (this.pending !== expected) return;
    clearTimeout(expected.timeoutId);
    this.pending = null;
    expected.resolve(value);
  }

  start(
    instruction: string,
    status: BridgeChatStatus,
    send: (instruction: string) => Promise<unknown>
  ): Promise<BridgeRunResult> {
    if (this.pending !== null || status === 'submitted' || status === 'streaming') {
      throw new BridgeBusyError();
    }

    // 前セッションのバッファ残存を確実に断ち切る。
    // BusyError で弾かれた start はここに到達しないため、正当な start だけがクリアを行う。
    this.messages = [];

    return new Promise<BridgeRunResult>((resolve, reject) => {
      const entry: McpBridgePending = {
        resolve,
        reject,
        processingConfirmed: false,
        startedWithError: status === 'error',
        timeoutId: setTimeout(() => {
          this.rejectPending(new Error('AIAssistantChat runner timed out'), entry);
        }, DEFAULT_TIMEOUT_MS),
      };

      this.pending = entry;

      // await せず .catch のみ繋ぐ。
      // sendMessage の完了を待たず Promise を返し、status 収束は handleStatusChange に委ねる。
      // .catch は sendMessage 自体の即時エラーだけを処理する。
      // try/catch は sendMessage が同期 throw した場合に this.pending が残存するのを防ぐ。
      try {
        send(instruction).catch((err: unknown) => {
          this.rejectPending(err instanceof Error ? err : new Error(String(err)), entry);
        });
      } catch (err: unknown) {
        this.rejectPending(err instanceof Error ? err : new Error(String(err)), entry);
      }
    });
  }

  handleStatusChange(input: HandleStatusChangeInput): void {
    const pending = this.pending;
    if (!pending) return;

    if (input.status === 'submitted' || input.status === 'streaming') {
      pending.processingConfirmed = true;
      return;
    }

    const result = evaluateBridgeChatConvergence({
      status: input.status,
      isAIResponseComplete: input.isAIResponseComplete,
      processingConfirmed: pending.processingConfirmed,
      startedWithError: pending.startedWithError,
    });

    switch (result.kind) {
      case 'pending':
        return;
      case 'resolved':
        this.resolvePending(
          { completedAt: new Date().toISOString(), messages: this.buildResultMessages(input.editToolFailureStopped) },
          pending
        );
        return;
      case 'error':
        this.rejectPending(new Error(`AI chat ended with status: ${input.status}`), pending);
        return;
      default: {
        const _exhaustive: never = result;
        throw new Error(`Unhandled ConvergenceResult: ${JSON.stringify(_exhaustive)}`);
      }
    }
  }

  // messages は要素 object まで含めた deep copy で渡す。配列だけ独立させても各 { text }
  // オブジェクトは shared 参照のため、呼び出し側が result.messages[i].text = '...' と
  // mutate すると session 内バッファも書き換わる。map で新規 object を作って参照も切る。
  //
  // 打ち切りで終わったターンはAIの発話が無いことがある。そのまま返すと呼び出し元は
  // 「編集が不要だった正常終了」と区別できず、黙って成功として扱ってしまう。
  // AIの発話に依存せずブリッジ側から打ち切りの事実を載せる。
  private buildResultMessages(editToolFailureStopped: boolean): MCPBridgeSpeech[] {
    const messages = this.messages.map((m) => ({ text: m.text }));
    if (editToolFailureStopped) {
      messages.push({ text: EDIT_TOOL_STOPPED_NOTICE });
    }
    return messages;
  }

  abort(reason: string): void {
    this.rejectImmediately(reason);
  }

  stop(): void {
    // 直接 reject で十分な理由：reject により pending=null になり、後続の handleStatusChange は
    // 冒頭の `if (!pending) return` で no-op になる。stop 後に SDK が ready に巻き戻しても
    // 二重 resolve は起きない。ユーザー停止は status/AI 応答を評価する収束判定とは責務が異なる
    // 制御指示なので、evaluateBridgeChatConvergence を経由させない。
    this.rejectImmediately('AIAssistantChat was stopped by user');
  }
}
